import React, { Suspense, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Login, Register, VerifyOTP, ForgotPassword, ResetPassword, CompleteProfile } from './components/auth';
import GmailCallback from './components/gmail/GmailCallback';
import { getPostLoginPath, getStoredUser, isSuperAdminUser, needsPhoneSetup } from './super-admin/auth';
import ChunkErrorBoundary from './components/common/ChunkErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';
import './App.css';

const Dashboard = lazyWithRetry(() => import(/* webpackPrefetch: true */ './components/common/Dashboard'));
const SuperAdminApp = lazyWithRetry(() => import('./super-admin/SuperAdminApp'));
const PublicCustomForm = lazyWithRetry(() => import('./components/integrations/PublicCustomForm'));

const AUTH_META = {
  '/login': ['Log In to Your Account | TracktCRM', 'Log in to TracktCRM to manage your leads, pipeline and follow-ups in one place.'],
  '/register': ['Create Your Free TracktCRM Account | TracktCRM', 'Sign up for TracktCRM free. No credit card needed. Capture leads, track your pipeline and never miss a follow-up.'],
  '/forgot-password': ['Forgot Your Password? | TracktCRM', "Forgot your TracktCRM password? Enter your account email and we'll send you a secure link to reset it."],
  '/reset-password': ['Set a New Password | TracktCRM', 'Set a new password for your TracktCRM account and get back to your leads, pipeline and follow-ups.'],
  '/complete-profile': ['Complete Your Profile | TracktCRM', 'Add your mobile number to finish setting up your TracktCRM account.'],
};

function DocumentRouteSync() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    const p = pathname.replace(/\/+$/, '') || '/';
    document.body.dataset.route = p;
    const [title, description] = AUTH_META[p] || ['TracktCRM', 'TracktCRM - Sales Lead CRM'];
    document.title = title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', description);
  }, [pathname]);
  useLayoutEffect(
    () => () => {
      delete document.body.dataset.route;
    },
    [],
  );
  return null;
}

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  const user = getStoredUser();
  if (isSuperAdminUser(user)) return <Navigate to="/super-admin/dashboard" replace />;
  if (needsPhoneSetup(user)) return <Navigate to="/complete-profile" replace />;
  return children;
}

function ProfileSetupRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  const user = getStoredUser();
  if (!needsPhoneSetup(user)) return <Navigate to={getPostLoginPath(user)} replace />;
  return children;
}

function PublicRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return children;
  const user = getStoredUser();
  return <Navigate to={getPostLoginPath(user)} replace />;
}

function SuperAdminProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <Router>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
      <DocumentRouteSync />
      <AppRoutes />
    </Router>
  );
}

/** Top-level pages; a failed page download shows a reload screen instead of a blank page. */
function AppRoutes() {
  const { pathname } = useLocation();
  return (
    <ChunkErrorBoundary resetKey={pathname}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/verify-otp"
          element={
            <PublicRoute>
              <VerifyOTP />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/complete-profile"
          element={
            <ProfileSetupRoute>
              <CompleteProfile />
            </ProfileSetupRoute>
          }
        />
        <Route path="/gmail-callback" element={<GmailCallback />} />
        <Route
          path="/f/:token"
          element={
            <Suspense fallback={<div className="pcf-page">Loading form…</div>}>
              <PublicCustomForm />
            </Suspense>
          }
        />
        <Route
          path="/super-admin/*"
          element={
            <SuperAdminProtectedRoute>
              <Suspense
                fallback={
                  <div className="sa-loading" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Loading…
                  </div>
                }
              >
                <SuperAdminApp />
              </Suspense>
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <div
                    style={{
                      minHeight: '100vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'system-ui, sans-serif',
                      color: '#666',
                    }}
                  >
                    Loading…
                  </div>
                }
              >
                <Dashboard />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Routes>
    </ChunkErrorBoundary>
  );
}

export default App;
